import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSeo } from "@/lib/api";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import Breadcrumb from "@/components/common/Breadcrumb";
import FinalCta from "@/components/common/FinalCta";
import { publishedArticles, articleCategories } from "@/data/articles";

export default function Conseils() {
  const { t, to, lang } = useLang();
  const [cat, setCat] = useState("all");
  useSeo({ lang, path: "/conseils", title: `${t.nav.conseils.label} — NEKASSUR`, description: t.conseils.intro });

  const all = publishedArticles();
  const list = cat === "all" ? all : all.filter((a) => a.category === cat);

  return (
    <>
      <section className="bg-nek-navy">
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: t.nav.conseils.label }]} />
          <Reveal className="mt-8 max-w-3xl">
            <Eyebrow className="!text-nek-teal-100">{t.conseils.eyebrow}</Eyebrow>
            <h1 className="nek-h1 mt-4 text-white" style={{ fontSize: "clamp(2.2rem,4vw,3.4rem)" }}>{t.conseils.pageTitle}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">{t.conseils.intro}</p>
          </Reveal>
        </div>
      </section>

      <Section tone="white">
        <div className="flex flex-wrap gap-2" data-testid="conseils-filters">
          <FilterBtn active={cat === "all"} onClick={() => setCat("all")}>{t.cta.allArticles}</FilterBtn>
          {articleCategories.map((c) => (
            <FilterBtn key={c} active={cat === c} onClick={() => setCat(c)}>{t.conseils.categories[c]}</FilterBtn>
          ))}
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {list.map((a) => (
            <Link key={a.slug} to={to(`/conseils/${a.slug}`)} data-testid={`article-card-${a.slug}`} className="nek-card group overflow-hidden hover:-translate-y-1 hover:shadow-lift">
              <div className="overflow-hidden">
                <img src={a.image} alt={a[lang].title} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold uppercase tracking-wider text-nek-blue">{t.conseils.categories[a.category]}</span>
                  <span className="text-nek-gray">· {a.readingTime} {t.conseils.readingTime}</span>
                </div>
                <h3 className="mt-2 text-base font-heading font-bold text-nek-navy">{a[lang].title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-nek-gray">{a[lang].excerpt}</p>
                <span className="link-arrow mt-3 text-sm">{t.cta.readMore} <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <FinalCta />
    </>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${active ? "bg-nek-navy text-white" : "border border-nek-border bg-white text-nek-navy hover:border-nek-blue"}`}>
      {children}
    </button>
  );
}
